import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import {
  getMyListings, getThreadPeers, getThreads, updateThread,
  type Thread, type ThreadMessage,
} from "@/api/me";
import { fetchListingById, type Listing } from "@/api/listings";

/* The conversations, once, for every surface that shows them.
 *
 * The inbox, the floating dock and the mobile sheet are three views of ONE
 * set of threads. Three copies of this logic would drift, and the first
 * symptom would be a reply appearing in one place and not another.
 *
 * WHAT IS CARRIED OVER, and why each rule exists -- all of it was learned the
 * hard way in the panel's own inbox and is kept verbatim in behaviour:
 *
 *   ORDER IS OURS. PostgREST returns rows in table order and an UPDATE moves
 *   a row, so replying reshuffled the list under the member's hand. Sorted by
 *   last activity, ties broken by id so it is stable between reads.
 *
 *   A FAILED READ IS NOT AN EMPTY INBOX. `threads` stays null on failure;
 *   setting [] told members they had no conversations when we simply could
 *   not fetch them.
 *
 *   "MINE" IS DECIDED BY ID, never by a name a message happens to carry.
 *
 *   A BARE ID IS NOT A TITLE. A thread whose listing will not resolve renders
 *   as a plain conversation, not as a uuid dressed up as a property.
 *
 *   SENDING IS OPTIMISTIC AND ROLLS BACK OUT LOUD. The bubble appears at
 *   once; a failure puts the text back in the box and says so.
 */

export const textOf = (m: ThreadMessage) => m.text ?? m.body ?? "";
export const whenOf = (m: ThreadMessage) => m.at ?? m.date ?? null;
export const senderOf = (m: ThreadMessage) => (m.from ?? m.sender ?? "").trim();

export function lastAt(th: Thread): number {
  const msgs = Array.isArray(th.messages) ? th.messages : [];
  let newest = 0;
  for (const m of msgs) {
    const ts = Date.parse(whenOf(m) ?? "");
    if (!Number.isNaN(ts) && ts > newest) newest = ts;
  }
  return newest;
}

const byRecency = (a: Thread, b: Thread) =>
  lastAt(b) - lastAt(a) || a.id.localeCompare(b.id);

export type ListingContext = { id: string; title: string; photo: string | null };

export type Chat = {
  threads: Thread[] | null;
  loading: boolean;
  failed: boolean;
  reload: () => void;
  peerName: (th: Thread) => string;
  contextOf: (th: Thread | null) => ListingContext | null;
  isMine: (m: ThreadMessage) => boolean;
  /** Appends to a thread. Resolves false on failure, having rolled back. */
  send: (threadId: string, text: string) => Promise<boolean>;
};

export function useThreads(fallbackName: string, deletedLabel = fallbackName): Chat {
  const { profile } = useAuth();
  const [threads, setThreads] = useState<Thread[] | null>(null);
  const [peers, setPeers] = useState<Record<string, string>>({});
  const [peersLoaded, setPeersLoaded] = useState(false);
  const [ctx, setCtx] = useState<Record<string, Listing>>({});
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!profile) { setThreads(null); setLoading(false); return; }
    let live = true;
    setLoading(true);
    setFailed(false);
    void getThreads()
      .then((r) => { if (live) { setThreads([...r].sort(byRecency)); } })
      .catch(() => { if (live) { setThreads(null); setFailed(true); } })
      .finally(() => { if (live) setLoading(false); });
    /* Names are decoration on top of the conversations. A failure here leaves
       the fallback label standing; it never fails the surface. */
    void getThreadPeers()
      .then((p) => { if (live) { setPeers(p); setPeersLoaded(true); } })
      .catch(() => {});
    return () => { live = false; };
  }, [profile, nonce]);

  /* One resolve pass per set of threads. Ids already held are not re-read, so
     a reply does not re-fetch the properties. */
  useEffect(() => {
    if (!threads) return;
    const want = [...new Set(threads.map((th) => th.listing_id)
      .filter((id): id is string => !!id))].filter((id) => !ctx[id]);
    if (!want.length) return;
    let live = true;
    void (async () => {
      const found = await Promise.all(want.map((id) => fetchListingById(id).catch(() => null)));
      const next: Record<string, Listing> = {};
      found.forEach((l, i) => { if (l) next[want[i]] = l; });
      /* Anything the public route will not serve is the member's own
         non-Active listing -- D-W8's rule, applied to the inbox. */
      if (found.some((l) => !l)) {
        try {
          for (const l of await getMyListings()) {
            if (want.includes(l.id) && !next[l.id]) next[l.id] = l;
          }
        } catch { /* leaves those threads as plain conversations */ }
      }
      if (live && Object.keys(next).length) setCtx((c) => ({ ...c, ...next }));
    })();
    return () => { live = false; };
  }, [threads, ctx]);

  const peerName = useCallback((th: Thread): string => {
    const embedded = th.counterpart?.name?.trim();
    if (embedded) return embedded;
    const other = th.participant_a === profile?.id ? th.participant_b : th.participant_a;
    const mapped = (other && peers[other]?.trim()) || "";
    if (mapped) return mapped;
    const named = (Array.isArray(th.messages) ? th.messages : [])
      .map(senderOf)
      .find((x) => x && x !== profile?.id && !/^[0-9a-f-]{36}$/i.test(x) && !x.includes("@"));
    if (named) return named;
    /* THE TOMBSTONE, and only when it is earned. `/my/thread-peers` answers
     * for every counterpart this member has; an id it does NOT answer for,
     * on a request that succeeded, is an account that is gone — Bassem's
     * ruling that messages stay with the other party and the sender shows as
     * «مستخدم محذوف».
     *
     * The `peersLoaded` guard is the whole difference between that and "we
     * could not fetch the names". Without it, one failed request would label
     * every counterpart on the page as deleted. */
    if (peersLoaded && other) return deletedLabel;
    return fallbackName;
  }, [peers, peersLoaded, profile, fallbackName, deletedLabel]);

  const contextOf = useCallback((th: Thread | null): ListingContext | null => {
    if (!th) return null;
    if (th.listing?.id) {
      return { id: th.listing.id, title: th.listing.title ?? "", photo: th.listing.photo_url ?? null };
    }
    const l = th.listing_id ? ctx[th.listing_id] : null;
    return l ? { id: l.id, title: l.title, photo: l.photoUrl } : null;
  }, [ctx]);

  const isMine = useCallback((m: ThreadMessage) => {
    const sender = senderOf(m);
    return !!profile
      && (sender === profile.id || sender.toLowerCase() === (profile.email ?? "").toLowerCase());
  }, [profile]);

  const send = useCallback(async (threadId: string, text: string): Promise<boolean> => {
    const body = text.trim();
    if (!body) return false;
    const current = threads?.find((t) => t.id === threadId);
    if (!current) return false;
    const before = Array.isArray(current.messages) ? current.messages : [];
    const next: ThreadMessage[] = [
      ...before,
      { from: profile?.id ?? "", text: body, at: new Date().toISOString() },
    ];
    setThreads((all) => (all ?? []).map((t) => (t.id === threadId ? { ...t, messages: next } : t)));
    try {
      await updateThread(threadId, next);
      return true;
    } catch {
      setThreads((all) => (all ?? []).map((t) => (t.id === threadId ? { ...t, messages: before } : t)));
      return false;
    }
  }, [threads, profile]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return useMemo(
    () => ({ threads, loading, failed, reload, peerName, contextOf, isMine, send }),
    [threads, loading, failed, reload, peerName, contextOf, isMine, send],
  );
}
